/*
 * The unified shell's page frame: the top bar, the rail and the content column on a fixed
 * full-viewport root with its own scroll. That is the shape the investor home views were
 * written for — `BouPageWithFrame` scrolls inside `#bou-tool-page-with-frame`, not the
 * document, and `scroll-utils` is keyed on that id — and the shape `withProtoFrame` gives the
 * prototypes this shell ports.
 *
 * It is not Primer's `PageLayout`, which the corporate shell is built on. That layout scrolls
 * the document and pads its columns to clear a fixed header; under it the investor views'
 * scroll helpers would point at an element that no longer scrolls, and the app root's
 * tablet padding for the legacy mobile header would open a band above the bar. Same slots,
 * same rail widths, a different scroll root — recorded in design/40-shell-and-pages.md
 * ("Shells by audience").
 *
 * One value the bar and the columns both read: `--unified-shell-header-height`. A literal,
 * because 50px is the design system's top-bar height (design/40-shell-and-pages.md "Top
 * bar") and it is on no token scale.
 */
.frame {
  --unified-shell-header-height: 50px;

  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: var(--color__white);
  /* The rail's two widths are container steps, and the frame is the container. */
  container-type: inline-size;
}

.body {
  display: flex;
  flex: 1 1 auto;
  min-block-size: 0;
}

/*
 * Primer's Pane `medium`: 256px from `md`, 296px from `lg` (design/40-shell-and-pages.md
 * "Sidebar"). Its own scroll, so a long rail cannot push the page taller than the window.
 */
.rail {
  flex: 0 0 auto;
  inline-size: 16rem;
  /* A transparent border keeps the layout slot while the stroke stops short of each end. */
  border-inline-end: 1px solid transparent;
  background-image: linear-gradient(var(--color__gray-300), var(--color__gray-300));
  background-repeat: no-repeat;
  background-position: right center;
  background-size: 1px calc(100% - 2 * var(--space__xl));
  background-origin: border-box;
  overflow-y: auto;
}

/*
 * An entity page's rail runs a solid divider from the identity band's bottom border to the foot
 * of the frame. The inset stroke above suits the home views, where nothing meets the rail's top
 * end; under the band it read as a broken line, a gap between two strokes that should join.
 */
.railSolid {
  border-inline-end-color: var(--color__gray-300);
  background-image: none;
}

@container (min-inline-size: 63.25rem) {
  .rail {
    inline-size: 18.5rem;
  }
}

/*
 * The rail's navigation and its foot in one column at least as tall as the rail, so the foot sits
 * at the bottom while the rows are short. Also the drawer's rail column below `md`.
 */
.railColumn {
  display: flex;
  flex-direction: column;
  min-block-size: 100%;
}

/*
 * The rail's utilities, behind a `gray-200` hairline as the `/chat` history sidebar's footer has
 * them (`chat/history/chat-history-sidebar.css`). `margin-block-start: auto` pushes the foot down
 * while the rail is shorter than the window, and `sticky` keeps it on screen once the rows scroll,
 * on white so they do not show through. The rail's own `lg` sides and `xl` bottom inset, so the
 * row's icon column lines up with the rows above. The label size is the rows' own step, which the
 * foot reads from the rail class it shares with them (`unified-investor-navigation.module.css`), so
 * the entry drops to 12px where they do. `:empty` collapses the foot, hairline included, for a user
 * the entry renders nothing for.
 */
.railFoot {
  position: sticky;
  inset-block-end: 0;
  margin-block-start: auto;
  padding: var(--space__sm) var(--space__lg) var(--space__xl);
  border-block-start: 1px solid var(--color__gray-200);
  background: var(--color__white);
  font-size: var(--unified-rail-row-font-size);
}

.railFoot:empty {
  display: none;
}

/*
 * White fading in over the last `2xl` of the rows, above the foot's hairline, while more rows are
 * below the fold (`data-rows-below-fold`, which `UnifiedShellFrame` keeps on the rail), so a row the
 * foot cuts off reads as the start of more rather than the end of the list (PLT-8068). On the foot,
 * which stays put, so the fade stays at the fold as the rows scroll; it lets clicks through. The
 * foot is `sticky`, so it is the fade's containing block, and `100%` plus the hairline puts the fade
 * just above it.
 */
.railFoot::before {
  content: '';
  position: absolute;
  inset-inline: 0;
  inset-block-end: calc(100% + 1px);
  block-size: var(--space__2xl);
  background: linear-gradient(to bottom, oklch(from var(--color__white) l c h / 0), var(--color__white));
  pointer-events: none;
  opacity: 0;
}

.rail[data-rows-below-fold] .railFoot::before {
  opacity: 1;
}

/*
 * `--unified-shell-gutter` is the page's inline gutter, set once here for every view in the column:
 * `2xl` beside the rail, `lg` below `md` where the rail has become the drawer
 * (design/10-foundations.md "A narrow container reduces its frame before its content"). Views that
 * also render in the legacy frame read it with a fallback, so the legacy page keeps its own value.
 */
.content {
  --unified-shell-gutter: var(--space__2xl);

  flex: 1 1 auto;
  min-inline-size: 0;
  overflow-y: auto;
}

/*
 * The chat view scrolls its own thread and pins its composer to the foot of the column, so the
 * column must not scroll as well: two nested scrollers would put the composer below the fold and
 * leave a second scrollbar beside the thread's own.
 */
.contentOwnScroll {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.spinner {
  padding-block-start: var(--space__5xl);
}

/*
 * Below `md` the rail leaves the layout entirely and becomes the drawer
 * (`unified-shell-drawer.tsx`, PLT-6738), opened from the menu button the top bar shows at the same
 * width. The content column is then the only thing in the body, so it takes the full width and
 * keeps the scroll behaviour it already has — which is why this rule is one line and the chat
 * surface needs no restatement here.
 *
 * This is the one place the unified shell departs from `design/10-foundations.md` "Layout and
 * responsive", which stacks the corporate shell's pane above its content at this width. That part
 * carries the departure and the argument for it; the short version is that this rail opens
 * expanded, holds a conversation list that grows, and sits beside a surface that pins its composer
 * to the foot of the viewport, so stacked it filled the whole phone and left every page below the
 * fold.
 *
 * The pane is hidden rather than unmounted, and `UnifiedShellFrame` moves the rail's *contents*
 * into the drawer instead — one mounted rail either way, because the rail owns a chat-runtime
 * subscription that must not run twice.
 */
@container (max-inline-size: 48rem) {
  .rail {
    display: none;
  }

  .content {
    --unified-shell-gutter: var(--space__lg);
  }
}

/*
 * The team-only status bar keeps its legacy 25px row at the foot of the frame, so the
 * internal-tools toggle (Alt+P) stays where team users find it. `BouStatusBar` positions
 * itself absolutely inside this row and lets the internal-tools bar grow upward out of it.
 * The row is rendered only when the `internalAdmin.showStatusBar` View setting is on — the
 * bar's own gate — so nobody else gets an empty strip.
 */
.statusBar {
  position: relative;
  flex: 0 0 auto;
  block-size: 25px;
}
